import { useEffect, useLayoutEffect, useRef, useState, type JSX } from 'react';
import type { TutorialApi } from './useTutorial.ts';
import { textFor } from './steps.ts';

interface TutorialOverlayProps {
  readonly tutorial: TutorialApi;
  /** On a phone each step speaks of taps and the on-screen buttons. */
  readonly phone?: boolean;
}

/** Space around the highlighted element, in CSS pixels. */
const PAD = 8;
const CARD_WIDTH = 320;
const GAP = 14;

interface Box {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

/**
 * One tutorial step on screen: everything dimmed but the element it is about,
 * and a card beside that element with what it does, how far through the
 * tutorial the coach is, Back, and Got it.
 *
 * While it is up, the board underneath does not take clicks or keys — the
 * coach reads, then confirms. Only Got it and Back move on; there is no way
 * to dismiss a step without reading it.
 */
export function TutorialOverlay({ tutorial, phone = false }: TutorialOverlayProps): JSX.Element | null {
  const { run } = tutorial;
  const step = run ? run.steps[run.index] : undefined;
  const [box, setBox] = useState<Box | null>(null);
  const [card, setCard] = useState<{ top: number; left: number } | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const gotItRef = useRef<HTMLButtonElement | null>(null);

  // Follow the element: layout can shift (a panel opens, the window resizes),
  // so its box is read every frame while a step is up.
  useEffect(() => {
    if (!step) return;
    let frame = 0;
    const follow = (): void => {
      const element = document.querySelector(`[data-tutorial="${step.target}"]`);
      const rect = element?.getBoundingClientRect();
      setBox((previous) =>
        rect &&
        (!previous || previous.top !== rect.top || previous.left !== rect.left || previous.width !== rect.width || previous.height !== rect.height)
          ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
          : previous,
      );
      frame = requestAnimationFrame(follow);
    };
    follow();
    return () => cancelAnimationFrame(frame);
  }, [step]);

  // Place the card beside the element, wherever it fits: below, above, right, left.
  useLayoutEffect(() => {
    const element = cardRef.current;
    if (!box || !element) return;
    const height = element.offsetHeight;
    const width = Math.min(CARD_WIDTH, window.innerWidth - 2 * GAP);
    const clampX = (x: number) => Math.min(Math.max(GAP, x), window.innerWidth - width - GAP);
    const clampY = (y: number) => Math.min(Math.max(GAP, y), window.innerHeight - height - GAP);
    const below = box.top + box.height + PAD + GAP;
    const above = box.top - PAD - GAP - height;
    const right = box.left + box.width + PAD + GAP;
    const left = box.left - PAD - GAP - width;
    const centreX = box.left + box.width / 2 - width / 2;
    const centreY = box.top + box.height / 2 - height / 2;
    if (below + height <= window.innerHeight - GAP) setCard({ top: below, left: clampX(centreX) });
    else if (above >= GAP) setCard({ top: above, left: clampX(centreX) });
    else if (right + width <= window.innerWidth - GAP) setCard({ top: clampY(centreY), left: right });
    else if (left >= GAP) setCard({ top: clampY(centreY), left });
    // Larger than the screen (the board itself): float the card over its corner.
    else setCard({ top: clampY(box.top + GAP * 2), left: clampX(box.left + GAP * 2) });
  }, [box, step]);

  // A new step takes focus, so Enter confirms it.
  useEffect(() => {
    if (step) gotItRef.current?.focus();
  }, [step]);

  // While a step is up the board's keyboard shortcuts stay off — Space must
  // not walk the play behind the tutorial. Enter and Tab still work on the
  // card's own buttons: their default actions are not stopped, only the
  // board's listeners are kept from hearing the key.
  useEffect(() => {
    if (!step) return;
    const swallow = (event: KeyboardEvent): void => {
      if (cardRef.current?.contains(event.target as Node)) return;
      event.stopPropagation();
      event.preventDefault();
    };
    const keepToCard = (event: KeyboardEvent): void => {
      if (!cardRef.current?.contains(event.target as Node)) return;
      if (event.key !== 'Enter' && event.key !== 'Tab') event.stopPropagation();
    };
    window.addEventListener('keydown', swallow, true);
    window.addEventListener('keydown', keepToCard, true);
    return () => {
      window.removeEventListener('keydown', swallow, true);
      window.removeEventListener('keydown', keepToCard, true);
    };
  }, [step]);

  if (!run || !step) return null;
  const last = run.index === run.steps.length - 1;

  return (
    <div className="tutorial" role="presentation">
      {box && (
        <div
          className="tutorial__spotlight"
          style={{ top: box.top - PAD, left: box.left - PAD, width: box.width + 2 * PAD, height: box.height + 2 * PAD }}
        />
      )}
      <div
        ref={cardRef}
        className="tutorial__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tutorial-title"
        // What this step is about, for anything that needs to follow along (pnpm verify).
        data-tutorial-step={step.id}
        data-tutorial-target={step.target}
        style={
          card
            ? { top: card.top, left: card.left, width: Math.min(CARD_WIDTH, window.innerWidth - 2 * GAP) }
            : { visibility: 'hidden' }
        }
      >
        <p className="tutorial__progress">
          {run.index + 1} of {run.steps.length}
        </p>
        <h2 id="tutorial-title" className="tutorial__title">
          {step.title}
        </h2>
        <p className="tutorial__text">{textFor(step, phone)}</p>
        <div className="tutorial__actions">
          <button type="button" className="btn btn--ghost" onClick={tutorial.back} disabled={run.index === 0}>
            Back
          </button>
          <button ref={gotItRef} type="button" className="btn btn--primary" onClick={tutorial.next}>
            {last ? 'Got it — done' : 'Got it'}
          </button>
        </div>
      </div>
    </div>
  );
}
